import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import supabase from '../supabaseClient';

// --- Módulo de Cache Centralizado (Poderia estar em src/utils/cache.js) ---
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutos (aumentado de 5 para 30)

class MemoryCache {
  constructor() {
    this.cache = new Map();
  }

  get(key) {
    const cachedItem = this.cache.get(key);
    if (!cachedItem || Date.now() > cachedItem.expiresAt) {
      if (cachedItem) this.cache.delete(key);
      return null;
    }
    return cachedItem.data;
  }

  set(key, data, duration = CACHE_DURATION_MS) {
    const expiresAt = Date.now() + duration;
    this.cache.set(key, { data, expiresAt });
  }

  delete(key) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }
}

const userCache = new MemoryCache();
const AUTH_CHANNEL = 'auth_channel';
const LAST_ACTIVE_KEY = 'mm_last_active_timestamp';

// --- Utilitários ---
const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => isDev && console.log(...args);
const logError = (...args) => console.error(...args);

// --- Contexto e Hook Principal ---
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// --- Componente Provedor ---
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const isInitialized = useRef(false);
  const wasHidden = useRef(false);
  const inactivityTimer = useRef(null);

  // --- Funções de Processamento e Atualização de Estado ---

  const processUserData = useCallback((profileData, userAccessData, authUser) => {
    const rolesMap = new Map();
    const permissionsSet = new Set();
    let userType = 'expositor';

    (userAccessData || []).forEach((access) => {
      if (!access.system_roles) return;
      const role = access.system_roles;
      rolesMap.set(role.id, { ...role, system_id: access.system_id });

      if (Array.isArray(role.permissions)) {
        role.permissions.forEach(p => permissionsSet.add(p));
      }

      if (['admin', 'administrador', 'Administrador CAEX'].includes(role.name)) {
        userType = 'admin';
      }
    });

    const roles = Array.from(rolesMap.values());
    const permissions = Array.from(permissionsSet);
    if (userType === 'admin') permissions.push('all');

    return {
      id: authUser.id,
      email: profileData.email,
      name: profileData.full_name,
      type: userType,
      roles,
      permissions,
      profile: profileData,
    };
  }, []);

  const setUserState = useCallback((userData) => {
    setUser(userData);
    setUserProfile(userData.profile);
    setUserRoles(userData.roles);
    setUserPermissions(userData.permissions);
  }, []);

  const clearUserData = useCallback(() => {
    setUser(null);
    setUserProfile(null);
    setUserRoles([]);
    setUserPermissions([]);
    userCache.clear();
    try {
      localStorage.removeItem('mm_user');
      localStorage.removeItem(LAST_ACTIVE_KEY);
    } catch (error) {
      logError('Erro ao limpar localStorage:', error);
    }
    log('🧹 Dados do usuário limpos.');
  }, []);

  // --- Lógica Principal de Carregamento de Dados ---

  const fetchAndProcessUserData = useCallback(async (authUser) => {
    log('🚀 Buscando dados do usuário no Supabase...');
    
    // ETAPA 1: Buscar o perfil do usuário
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .eq('active', true)
      .single();

    if (profileError || !profileData) {
      throw new Error('Perfil não encontrado ou inativo.');
    }

    // ETAPA 2: Buscar os acessos e roles do usuário
    const { data: userAccessData, error: accessError } = await supabase
      .from('user_system_access')
      .select('*, system_roles(*)') // Consulta corrigida e mais segura
      .eq('profile_id', authUser.id)
      .eq('active', true)
      .or('expires_at.is.null,expires_at.gt.now()');

    if (accessError) {
      throw new Error('Erro ao buscar acessos do usuário.');
    }

    if (!userAccessData || userAccessData.length === 0) {
        throw new Error('Usuário não possui acesso válido ao sistema');
    }

    const processedData = processUserData(profileData, userAccessData, authUser);
    
    userCache.set(authUser.id, processedData);
    setUserState(processedData);
    
    try {
      localStorage.setItem('mm_user', JSON.stringify(processedData));
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    } catch (storageError) {
      logError('Erro ao salvar no localStorage:', storageError);
    }

    log('🎉 Dados do usuário carregados e cacheados com sucesso.');
    return processedData;
  }, [processUserData, setUserState]);

  const loadUserData = useCallback(async (authUser, { forceRefresh = false } = {}) => {
    const cacheKey = authUser.id;
    const cachedData = userCache.get(cacheKey);

    if (cachedData && !forceRefresh) {
      log('📦 Usando dados do cache e revalidando em segundo plano...');
      setUserState(cachedData);
      setLoading(false);
      
      fetchAndProcessUserData(authUser).catch(err => {
        logError('Falha na revalidação em segundo plano:', err);
      });
      return;
    }

    setLoading(true);
    try {
      await fetchAndProcessUserData(authUser);
    } catch (error) {
      logError('💥 Erro ao carregar dados do usuário:', error);
      clearUserData();
    } finally {
      setLoading(false);
    }
  }, [fetchAndProcessUserData, setUserState, clearUserData]);

  // --- Verificação da sessão OTIMIZADA ---
  const checkSession = useCallback(async () => {
    if (!user) return;
    
    log('🔍 Verificando sessão após retorno à aba...');
    
    // MUDANÇA PRINCIPAL: Não definir loading imediatamente
    // Só definir loading se realmente for necessário fazer refresh
    
    try {
      // Verificar se a sessão ainda é válida
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Verificar quanto tempo se passou desde a última atividade
        const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0');
        const timeInactive = Date.now() - lastActive;
        
        // MUDANÇA: Tolerância muito maior para evitar refreshes desnecessários
        const EXTENDED_INACTIVITY_THRESHOLD = 2 * 60 * 60 * 1000; // 2 horas (era 30 min)
        
        if (timeInactive > EXTENDED_INACTIVITY_THRESHOLD) {
          log('⏰ Inatividade prolongada detectada (>2h), recarregando dados...');
          setLoading(true); // SÓ AGORA definir loading
          await loadUserData(session.user, { forceRefresh: true });
          wasHidden.current = false;
        } else {
          log('✅ Sessão válida e dados recentes, sem necessidade de refresh');
          // Apenas atualizar timestamp sem causar re-renderização
          localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
          wasHidden.current = false;
        }
      } else {
        log('🚫 Sessão expirada durante inatividade');
        clearUserData();
      }
    } catch (error) {
      logError('Erro ao verificar sessão:', error);
      // Em caso de erro, não limpar dados do usuário
      // Apenas logar e continuar para não interromper o usuário
    }
    
    // Sempre atualizar timestamp da última atividade
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
  }, [user, loadUserData, clearUserData]);

  // --- Efeitos de Inicialização e Sincronização OTIMIZADOS ---

  // Gerenciamento de visibilidade da aba OTIMIZADO
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        log('🙈 Aba ficou oculta');
        wasHidden.current = true;
        
        // Limpar qualquer timer existente
        if (inactivityTimer.current) {
          clearTimeout(inactivityTimer.current);
        }
      } else if (document.visibilityState === 'visible') {
        log('👁️ Aba ficou visível novamente');
        
        // MUDANÇA PRINCIPAL: Verificação mais inteligente
        // Só verificar sessão se realmente necessário
        const timeSinceHidden = Date.now() - (parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0'));
        const CRITICAL_INACTIVITY_THRESHOLD = 60 * 60 * 1000; // 1 hora
        
        // Só forçar verificação se:
        // 1. Ficou oculta por mais de 1 hora, OU
        // 2. Não há timestamp de última atividade
        if (timeSinceHidden > CRITICAL_INACTIVITY_THRESHOLD || !localStorage.getItem(LAST_ACTIVE_KEY)) {
          log('⚠️ Inatividade crítica detectada (>1h) ou sem timestamp, verificando sessão...');
          inactivityTimer.current = setTimeout(() => {
            checkSession();
          }, 100);
        } else {
          // Apenas atualizar timestamp sem verificação completa
          localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
          wasHidden.current = false;
          log('✅ Retorno rápido à aba, sessão considerada válida');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [checkSession]);

  useEffect(() => {
    const handleOnline = () => { 
      log('✅ Online'); 
      setIsOnline(true);
      // MUDANÇA: Só verificar sessão se offline por muito tempo
      const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0');
      const timeOffline = Date.now() - lastActive;
      if (timeOffline > 10 * 60 * 1000) { // 10 minutos offline
        checkSession();
      }
    };
    
    const handleOffline = () => { 
      log('🔌 Offline'); 
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    const handleAuthMessage = (event) => {
      if (event.data.type === 'LOGOUT') {
        log('🔄 Recebido evento de logout de outra aba.');
        clearUserData();
      }
    };
    channel.addEventListener('message', handleAuthMessage);

    const initializeAuth = async () => {
      if (isInitialized.current) return;
      isInitialized.current = true;

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        log('🔍 Sessão ativa encontrada, carregando dados...');
        await loadUserData(session.user);
        // Inicializar o timestamp de atividade
        localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      } else {
        log('🚫 Nenhuma sessão ativa.');
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      log(`🔄 Evento de autenticação: ${event}`);
      if (event === 'SIGNED_IN' && session?.user) {
        await loadUserData(session.user);
        localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      } else if (event === 'SIGNED_OUT') {
        clearUserData();
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      channel.removeEventListener('message', handleAuthMessage);
      channel.close();
    };
  }, [loadUserData, clearUserData, checkSession]);

  // --- Funções Expostas pelo Contexto ---

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
        // Mapeamento de erros para mensagens mais claras
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Credenciais inválidas.');
        }
        throw new Error('Erro de autenticação ou problema de rede.');
    }
    // O `onAuthStateChange` cuidará do carregamento dos dados após o SIGNED_IN
    return data;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    // O onAuthStateChange cuidará de limpar os dados, mas podemos forçar aqui também
    clearUserData();
    
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage({ type: 'LOGOUT' });
    channel.close();
  }, [clearUserData]);

  const hasRole = useCallback((roleName) => {
    if (!userRoles?.length) return false;
    const roleMapping = {
      'admin': ['admin', 'administrador', 'Administrador CAEX'],
      'expositor': ['expositor', 'Expositor'],
    };
    const allowedRoles = roleMapping[roleName] || [roleName];
    return userRoles.some(role => allowedRoles.includes(role.name));
  }, [userRoles]);

  const hasPermission = useCallback((permissionName) => {
    if (!userPermissions?.length) return false;
    return userPermissions.includes('all') || userPermissions.includes(permissionName);
  }, [userPermissions]);

  // Função para forçar o refresh dos dados
  const refreshUserData = useCallback(async () => {
    if (!user) return;
    
    log('🔄 Forçando atualização dos dados do usuário...');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      await loadUserData(session.user, { forceRefresh: true });
      return true;
    }
    return false;
  }, [user, loadUserData]);

  // --- Valor do Contexto Memoizado ---

  const contextValue = useMemo(() => ({
    user,
    userProfile,
    userRoles,
    userPermissions,
    loading,
    isOnline,
    login,
    logout,
    hasRole,
    hasPermission,
    refreshUserData, // Nova função exposta
  }), [
    user, 
    userProfile, 
    userRoles, 
    userPermissions, 
    loading, 
    isOnline, 
    login, 
    logout, 
    hasRole, 
    hasPermission,
    refreshUserData
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};